/**
 * The finish pass: repairs that need no judgment, made on the collected descriptors so a build cannot ship what they catch.
 *
 * - type under the floor is lifted to it (`buildTypeFloorM`: the share of the unit width that the composer and the quality checks read from
 *   `src/layout/legibility`, or the physical floor of a narrow unit, whichever is larger);
 * - text that fails the minimum contrast on the surface behind it (4.5:1; 3:1 for WCAG large text) takes that surface's own text colour,
 *   else black or white;
 * - primary text (above the caption rung) under the prototype's 7:1 (STYLE_DESIGN_PRINCIPLES.md: primary text AAA, secondary AA) takes a
 *   text colour of the palette that reaches it, its surface's own first; where none does, AA stands as the floor. Large text is held to
 *   3:1 here too (`LARGE_TEXT_AA`);
 * - a character the text's face cannot draw (it comes out as blank space) is reported, not repaired: the quality checks' `glyph` rule,
 *   read from the build;
 * - a Layout container below the unit that holds a Fixed width or height is released to Hug, so it grows with what it holds instead of
 *   overflowing. A stage (its Layout carries a `ContentSlot`) and an image (a `LayoutItem`, sized from its aspect) are not containers in
 *   this sense and keep their Fixed height; so does an Absolute body.
 *
 * It reads descriptors only, so any path that produces them can call it. Each repair is reported in the returned notes.
 */
import type { SpatialObjectDescriptor } from './spatial-types';
import { RAMP_LIFT, THEME_DEFAULTS, liftToFloor, rungType, typeEmM } from './design-tokens';
import { WCAG_AA_TEXT_CONTRAST, contrastRatio, pickForeground } from '../../layout/legibility';
import { missingGlyphs } from '../../behaviors/text/measure';
import type { FontStyle, FontWeight } from '../../scenedata/behaviors/textdata';

/**
 * The prototype's bar for primary text on its surface (AILayout_Prototype docs/STYLE_DESIGN_PRINCIPLES.md, Legibility Rules), for text
 * below WCAG's large-text size. Large text is held to `LARGE_TEXT_AA` instead, on every blocks build.
 */
export const PRIMARY_TEXT_CONTRAST = 7;
/**
 * WCAG's large-text bar: 3:1 (AA) for text of at least 24 px, or 18.66 px bold, at the prototype's own scale (before the AR lift). Large
 * text is held to it in both steps: the AA step (4.5:1 for other text) and the primary-text step (7:1 for other text). Holding a headline
 * to 7:1 recoloured every accent headline under it (an orange on cream) to the ink; WCAG's own large-text AAA (4.5:1) would still repair
 * those at 3 to 4.5:1. Headlines keep their colour down to 3:1; body text keeps 4.5 and 7. The flag is read from the root, which on the
 * blocks surface is the unit: its width is read from the same place.
 */
export const LARGE_TEXT_AA = 3;
const LARGE_PX = 24;
const LARGE_BOLD_PX = 18.66;

/** Whether a Text of this size (`fontSize`, centi-world-units) in a unit this wide (metres) is large text by WCAG: at least the size 24 px is set at. */
export function isLargeText(fontSize: number, unitWidth: number, bold: boolean, lift: number = RAMP_LIFT): boolean {
    // Half of `rungType`'s 1e-4 rounding, so a rung set at exactly that size counts.
    const atLeast = (px: number) => fontSize >= typeEmM(px, unitWidth, lift) * 100 - 5e-5;
    return atLeast(LARGE_PX) || (bold && atLeast(LARGE_BOLD_PX));
}

type Bag = Record<string, unknown>;
const bagOf = (d: SpatialObjectDescriptor, type: string): Bag | undefined => d.behaviors?.find(b => b.type === type)?.overrides;
const HEX = /^#[0-9a-fA-F]{6}$/;

export function finishBuild(objects: readonly SpatialObjectDescriptor[]): string[] {
    const notes: string[] = [];
    const blanks = new Map<string, { code: number; family: string | undefined; first: string; texts: number }>();
    const byId = new Map(objects.map(o => [o.id, o]));
    const rootOf = (d: SpatialObjectDescriptor): SpatialObjectDescriptor => {
        let at = d;
        for (let up = byId.get(at.parentId ?? ''); up; up = byId.get(at.parentId ?? '')) at = up;
        return at;
    };
    const fills = new Map<string, string>();
    for (const d of objects) {
        const fill = bagOf(d, 'Renderer')?.materials;
        const face = Array.isArray(fill) ? (fill[0] as Bag | undefined)?.baseColorFactor : undefined;
        if (d.parentId && typeof face === 'string' && bagOf(d, 'LayoutItem')?.positionMode === 'Absolute' && !fills.has(d.parentId)) fills.set(d.parentId, face);
    }
    const themeOf = (root: SpatialObjectDescriptor): Record<string, string> => {
        const declared = bagOf(root, 'Properties')?.properties;
        const out = { ...THEME_DEFAULTS };
        if (Array.isArray(declared)) for (const p of declared as Bag[]) if (typeof p.name === 'string' && typeof p.value === 'string') out[p.name] = p.value;
        return out;
    };
    const token = (v: string) => /^\{(\w+)\}$/.exec(v)?.[1] ?? /^\$(\w+)$/.exec(v)?.[1];

    for (const d of objects) {
        const root = rootOf(d);
        const text = bagOf(d, 'Text');
        if (text) {
            const width = bagOf(root, 'Layout')?.width;
            if (typeof text.fontSize === 'number' && typeof width === 'number') {
                const lifted = liftToFloor(text.fontSize, width);
                if (lifted > text.fontSize) {
                    notes.push(`"${d.name}": type ${text.fontSize} was under the legibility floor for this unit; set to ${lifted}.`);
                    text.fontSize = lifted;
                }
            }
            const theme = themeOf(root);
            const family = typeof text.fontFamily === 'string' ? theme[token(text.fontFamily) ?? ''] ?? text.fontFamily : undefined;
            const missing = typeof text.text === 'string' ? missingGlyphs(text.text, {
                fontFamily: family, fontWeight: text.fontWeight as FontWeight | undefined, fontStyle: text.fontStyle as FontStyle | undefined,
            }) : [];
            for (const c of missing) {
                const key = `${c} ${family ?? ''}`;
                const seen = blanks.get(key);
                if (seen) seen.texts++;
                else blanks.set(key, { code: c, family, first: d.name, texts: 1 });
            }
            const resolve = (v: unknown): string | undefined => {
                if (typeof v !== 'string') return undefined;
                const t = token(v);
                const hex = t === undefined ? v : theme[t];
                return typeof hex === 'string' && HEX.test(hex) ? hex : undefined;
            };
            const large = typeof text.fontSize === 'number' && typeof width === 'number' && isLargeText(text.fontSize, width, text.fontWeight === 'bold', root.faithfulScale ? 1 : RAMP_LIFT);
            const aaBar = large ? LARGE_TEXT_AA : WCAG_AA_TEXT_CONTRAST;
            const aaaBar = large ? LARGE_TEXT_AA : PRIMARY_TEXT_CONTRAST;
            const fg = resolve(text.color);
            let surface: string | undefined;
            for (let at: SpatialObjectDescriptor | undefined = d; at && !surface; at = byId.get(at.parentId ?? '')) surface = fills.get(at.id);
            const bg = resolve(surface);
            if (fg && bg && contrastRatio(fg, bg) < aaBar) {
                const role = surface !== undefined ? /^(\w+)Color$/.exec(token(surface) ?? '')?.[1] : undefined;
                const pair = role ? `{${role}TextColor}` : undefined;
                const repaired = pair !== undefined && resolve(pair) !== undefined && contrastRatio(resolve(pair)!, bg) >= aaBar ? pair : pickForeground(bg);
                notes.push(`"${d.name}": text ${String(text.color)} reads ${contrastRatio(fg, bg).toFixed(1)}:1 on its surface; set to ${repaired}.`);
                text.color = repaired;
            }
            const now = resolve(text.color);
            // Primary is above the ramp's caption size, `rungType('caption')`, and a page kept to its own px is measured against it too:
            // its text is primary from about 22 px in a 1.2 m unit, and from 13 px in a 0.4 m one, where both sit on the physical floor.
            const primary = typeof text.fontSize === 'number' && typeof width === 'number' && text.fontSize > rungType('caption', width).fontSize;
            if (primary && now && bg && contrastRatio(now, bg) < aaaBar) {
                const role = surface !== undefined ? /^(\w+)Color$/.exec(token(surface) ?? '')?.[1] : undefined;
                const palette = Object.keys(theme).filter(k => k.endsWith('TextColor')).map(k => `{${k}}`);
                const own = role ? `{${role}TextColor}` : undefined;
                const reads = (c: string) => contrastRatio(resolve(c) ?? now, bg);
                const best = palette.filter(c => resolve(c) && reads(c) >= aaaBar)
                    .sort((a, b) => Number(b === own) - Number(a === own) || reads(b) - reads(a))[0];
                if (best !== undefined) {
                    notes.push(`"${d.name}": primary text reads ${contrastRatio(now, bg).toFixed(1)}:1 on its surface, under ${aaaBar}:1; set to ${best} (${reads(best).toFixed(1)}:1).`);
                    text.color = best;
                }
            }
        }
        if (d === root || d.imagePrompt) continue;
        const box = bagOf(d, 'Layout');
        if (!box || box.positionMode === 'Absolute' || d.keepFixed || d.behaviors?.some(b => b.type === 'ContentSlot')) continue;
        for (const axis of ['width', 'height'] as const) {
            if (box[`${axis}Mode`] !== 'Fixed') continue;
            notes.push(`"${d.name}": ${axis} was Fixed below the unit; released to Hug so it holds its content.`);
            box[`${axis}Mode`] = 'Hug';
            delete box[axis];
        }
    }
    // One note per missing character and family, not per text: thirty bullets would otherwise drown the other notes. A family's weights
    // and styles share one charset, so the family is the key.
    for (const { code, family, first, texts } of blanks.values()) {
        const more = texts > 1 ? ` (and ${texts - 1} more ${texts === 2 ? 'text' : 'texts'})` : '';
        notes.push(`"${first}"${more}: ${String.fromCodePoint(code)} U+${code.toString(16).toUpperCase().padStart(4, '0')} not in ${family ?? 'the default face'}, `
            + 'so it draws as blank space. Use characters the face has, words, or (in html()) an inline <svg>.');
    }
    return notes;
}
